/* eslint-disable import/prefer-default-export */
import NumberFormatter from 'bloko/common/numberFormatter';

import { NARROW_NON_BREAKING_SPACE } from 'src/constants/symbols';

type NullableNumber = number | null | undefined;

export const DEFAULT_VALUE = '–';

export const isDefined = (value: NullableNumber): value is number => {
    return value !== null && value !== undefined;
};

/**
 * Округляет число до заданной точности
 */
export const roundNumber = (value: NullableNumber, decimalLength = 0): number | null => {
    if (!isDefined(value)) {
        return null;
    }
    const factor = Number(`1e${decimalLength}`);
    return Math.round(value * factor) / factor;
};

/**
 * Форматирует число в удобный для чтения формат. Пример '99 999,99'
 * Округляет до заданной точности, разбивает разряды по группам.
 * По умолчанию выводит '–', если само число не задано.
 */
export interface FormatNumberParams {
    withDefaultValue?: boolean;
    postfix?: string;
    decimalLength?: number;
    fixed?: boolean;
}

const formatToNumber = (value: NullableNumber, params: FormatNumberParams = {}): string | null => {
    const { withDefaultValue = true, postfix, decimalLength = 0, fixed = false } = params;

    if (!isDefined(value)) {
        return withDefaultValue ? DEFAULT_VALUE : null;
    }

    const roundedValue = roundNumber(value, decimalLength);

    const result = fixed ? roundedValue?.toFixed(decimalLength) : roundedValue;

    return `${NumberFormatter.format(String(result), {
        groupSeparator: NARROW_NON_BREAKING_SPACE,
        decimalLength,
    })}${postfix || ''}`;
};

/**
 * Форматирует число в удобный для чтения формат.
 * Округляет до целых, разбивает разряды по группам.
 * По умолчанию выводит '–', если само число не задано.
 */
export const formatToInt = (value: NullableNumber, params: FormatNumberParams = {}): string | null => {
    return formatToNumber(value, { ...params, decimalLength: 0 });
};

/**
 * Форматирует число в удобный для чтения формат.
 * Округляет до 2 знаков после запятой, разбивает разряды по группам.
 * По умолчанию выводит '–', если само число не задано.
 */
export const formatToFloat = (value: NullableNumber, params: FormatNumberParams = {}): string | null => {
    return formatToNumber(value, { decimalLength: 2, ...params });
};

/**
 * Переводит абсолютное значение в проценты. Например, 0.55678 -> '55,68%'
 * Округляет до 2 знаков после запятой. Добавляет символ '%' в конце.
 * По умолчанию выводит '–', если само число не задано.
 */
export const formatToPercentage = (value: NullableNumber, params: FormatNumberParams = {}): string | null => {
    const { withDefaultValue = true, postfix = '%', decimalLength = 2 } = params;

    if (!isDefined(value)) {
        return withDefaultValue ? DEFAULT_VALUE : null;
    }

    const result = formatToNumber(value * 100, { decimalLength });
    if (!result) {
        return null;
    }
    return `${result}${postfix || ''}`;
};
